<template>
  <v-card>
    <v-toolbar flat color="white" density="compact">
      <v-text-field
          density="compact"
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          color="primary"
          v-model="search"
          hide-details
          single-line
          flat
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('search') }}</span>
        </template>
      </v-text-field>
      <v-autocomplete
          v-if="show_platform_dropdown"
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="advanced_search.root_asset_name"
          :items="advanced_search_models.root_asset_name"
          @update:model-value="showRecords"
          :return-object="false"
          hide-details
          single-line
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('root_asset_name') }}</span>
        </template>
      </v-autocomplete>
      <v-autocomplete
          v-if="show_platform_dropdown"
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="advanced_search.cluster"
          :items="advanced_search_models.cluster"
          @update:model-value="showRecords"
          :return-object="false"
          hide-details
          single-line
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('cluster') }}</span>
        </template>
      </v-autocomplete>
      <v-spacer/>
    </v-toolbar>
    <div style="overflow-x: auto; width: 100%;">
      <v-data-table
          :headers="headers"
          :items="getRecords"
          class="elevation-1"
          @click:row="handleClick"
          :search="search"
          v-bind="footer_props"
          v-model:items-per-page="options.itemsPerPage"
          v-model:page="options.page"
          :show-select="false"
          :group-by="[{key: 'article_id', order: 'asc'}]">
        <template #group-header="{item, columns, toggleGroup, isGroupOpen}">
          <tr>
            <td :colspan="columns.length">
              <v-btn size="small" variant="flat" @click="toggleGroup(item)" :ref="'group-' + item.value" :data-open="isGroupOpen(item)">
                <v-icon :icon="isGroupOpen(item) ? '$expand' : '$next'" size="x-large"/>
                {{ item.items[0].raw.group_name }}
              </v-btn>
            </td>
          </tr>
        </template>
        <template #item.distribution_device_function_name="{ item }">
          {{ $store.getters.translate(item.distribution_device_function_name) }}
        </template>
        <template #item.result="{ item }">
          <v-chip v-if="item.result === 'Review'" size="small" color="primary" variant="tonal">
            {{ item.result }}
          </v-chip>
          <v-chip v-else-if="item.result === 'Deviation'" size="small" color="warning" variant="tonal">
            {{ item.result }}
          </v-chip>
          <v-chip v-else-if="item.result === 'Compliant'" size="small" color="success" variant="tonal">
            {{ item.result }}
          </v-chip>
        </template>
        <template #item.actions="{ item }">
          <v-menu>
            <template #activator="{ props }">
              <v-btn elevation="0" icon v-bind="props">
                <PhDotsThreeVertical size="24" style="cursor: pointer;"/>
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-if="canEdit" @click="$emit('edit', item)">
                <template #prepend><PhPencilSimpleLine size="24"/></template>
                <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('edit') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canDelete" @click="deleteRecord(item.id)">
                <template #prepend><PhTrash size="24"/></template>
                <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('delete') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </div>
  </v-card>
</template>

<script>
import {useAbility} from "@casl/vue";
import helpFunctions from "../../plugins/helpFunctions";
import { PhDotsThreeVertical, PhPencilSimpleLine, PhTrash } from "@phosphor-icons/vue";

export default {
  components: {
    PhTrash, PhPencilSimpleLine, PhDotsThreeVertical,
  },
  props: ["show_platform_dropdown"],
  setup() {
    const { can } = useAbility()
    return { can }
  },
  data() {
    return {
      page: helpFunctions.pages.measurements,
      headers: [],
      all_records: [],
      records: [],
      options: helpFunctions.table_options,
      footer_props: helpFunctions.footer_props,
      search: '',
      advanced_search: {
        root_asset_name: null,
        cluster: null,
      },
      advanced_search_models: {
        root_asset_name: [],
        cluster: [],
      },
    };
  },
  created() {
    this.headers = helpFunctions.composeTableHeaders(helpFunctions.headers.measurements);
  },
  methods: {
    fillAdvancedSearchArrays() {
      this.all_records.forEach(record => {
        if(record.root_asset_name && !this.advanced_search_models.root_asset_name.includes(record.root_asset_name)) {
          this.advanced_search_models.root_asset_name.push(record.root_asset_name);
        }
        if(record.cluster && !this.advanced_search_models.cluster.includes(record.cluster)) {
          this.advanced_search_models.cluster.push(record.cluster);
        }
      });
      this.showRecords();
    },
    showRecords() {
      let sorted_array = this.all_records;
      if(this.advanced_search.root_asset_name) {
        sorted_array = sorted_array.filter(item => item.root_asset_name === this.advanced_search.root_asset_name);
      }
      if(this.advanced_search.cluster) {
        sorted_array = sorted_array.filter(item => item.cluster === this.advanced_search.cluster);
      }
      this.records = sorted_array;
      this.openAll();
    },
    handleClick(event, row) {
      this.$emit('edit', row.item);
    },
    async deleteRecord(id) {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async (res) => {
        if (res) {
          await helpFunctions.deleteModel(this.$toast, this.page.name, id);
          this.$emit('refresh');
        }
      });
    },
    openAll() {
      this.$nextTick(() => {
        Object.keys(this.$refs).forEach(k => {
          if (k.startsWith('group-') && this.$refs[k] && !this.$refs[k].$attrs['data-open']) {
            this.$refs[k].$el.click();
          }
        });
      });
    },
  },
  computed: {
    getRecords() {
      if(this.records) {
        return this.records;
      }
      else {
        return [];
      }
    },
    canEdit() {
      return this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canDelete() {
      return this.can("delete", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
  },
  watch: {
    options: {
      handler() {
        this.showRecords();
      },
      deep: true,
    },
    advanced_search: {
      handler() {
        this.options.page = 1;
      },
      deep: true,
    },
  },
};
</script>